import { ITooltipParams, ValueFormatterParams, ValueGetterParams } from "@ag-grid-community/core";
import { get } from "lodash-es";

import { EntityType } from "@mapmycustomers/shared/enum";
import CountryCode from "@mapmycustomers/shared/enum/CountryCode";
import MonetaryValue from "@mapmycustomers/shared/types/customField/MonetaryValue";
import {
  Activity,
  Company,
  Deal,
  EntitySupportingCustomFields,
  EntityTypeSupportingFiles,
  Person,
  Trip,
} from "@mapmycustomers/shared/types/entity";
import File from "@mapmycustomers/shared/types/File";
import Team from "@mapmycustomers/shared/types/Team";
import User, { UserRef } from "@mapmycustomers/shared/types/User";

import ConfigService from "@app/config/ConfigService";
import i18nService from "@app/config/I18nService";
import localSettings from "@app/config/LocalSettings";
import Path from "@app/enum/Path";
import { getUsers } from "@app/store/members";
import { getCurrencies } from "@app/store/referenceData";
import { getStoreState } from "@app/store/storeRef";
import { formatDate, formatDistanceInMeters } from "@app/util/formatters";

import formatCountryName from "../../countries/formatCountryName";
import isValidDate from "../../isValidDate";

export const valueGetterUsingFunction =
  (fn: (entity: unknown) => unknown) => (params: ValueGetterParams) =>
    params.data ? fn(params.data) : undefined;

export const valueFormatterUsingFunction =
  (fn: (entity: unknown, value: unknown) => string) => (params: ValueFormatterParams) =>
    params.data ? fn(params.data, params.value) : "";

export const defaultTooltipValueGetter = (fieldName: string) => (params: ITooltipParams) => {
  if (!params.data) {
    return "";
  }
  if (params.valueFormatted !== undefined) {
    return params.valueFormatted;
  }
  if (params.value !== undefined) {
    return params.value;
  }
  return params.data[fieldName] ?? "";
};

// TODO: move these date-time formatters somewhere away
export const dateTimeFormatter = (entity: unknown, date: unknown) =>
  isValidDate(date) ? formatDate(date, "PPp") : "";

export const dateFormatter = (entity: unknown, date: unknown) =>
  isValidDate(date) ? formatDate(date, "PP") : "";

export const timeFormatter = (entity: unknown, date: unknown) =>
  isValidDate(date) ? formatDate(date, "p") : "";

export const booleanFormatter = (entity: unknown, value: unknown) => (value ? "Yes" : "No");

export const listFormatter =
  (path: string, separator: string = ", ") =>
  (entity: unknown, values: unknown) =>
    Array.isArray(values) ? values.map((value) => get(value, path, "")).join(separator) : "";

export const locationFormatter = (entity: unknown, coordinates: unknown) =>
  Array.isArray(coordinates) ? `${coordinates[1]}, ${coordinates[0]}` : "";

export const moneyFormatter = (entity: unknown, fieldValue: unknown): string => {
  const value = fieldValue as number;
  const currencyCode = localSettings.getCurrencyCode();
  if (!currencyCode || !Number.isFinite(value)) {
    return "";
  }

  return (
    i18nService.getIntl()?.formatNumber(value, {
      currency: currencyCode,
      minimumFractionDigits: 0,
      style: "currency",
    }) ?? ""
  );
};

export const currencyFormatter = (entity: unknown, fieldValue: unknown): string => {
  const { currencyId, value } = fieldValue as MonetaryValue;
  const currency = getCurrencies(getStoreState()).find(({ id }) => id === currencyId);
  if (!currency) {
    return "";
  }
  if (!Number.isFinite(value)) {
    return currency.code;
  }

  return (
    i18nService.getIntl()?.formatNumber(value, {
      currency: currency.code,
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
      style: "currency",
    }) ?? ""
  );
};

export const monetaryAmountGetter =
  (fn: (entity: EntitySupportingCustomFields) => MonetaryValue) => (params: ValueGetterParams) =>
    fn(params.data).value;

export const meterDistanceValueFormatter = (entity: unknown, value: unknown) => {
  const intl = i18nService.getIntl();
  return intl ? formatDistanceInMeters(intl, value as number) : "";
};

export const distanceFormatter = (entity: unknown, distance: unknown) => {
  if (!distance && distance !== 0) {
    return "";
  }
  return meterDistanceValueFormatter(entity, (distance as number) * 1000);
};

export const getUserTeams = (userId?: UserRef["id"]): Team[] => {
  if (!userId) {
    return [];
  }
  const foundUser: undefined | User = getUsers(getStoreState()).find(({ id }) => id === userId);
  if (!foundUser) {
    return [];
  }
  return foundUser.teams;
};

export const countryFormatter = (fieldValue: unknown): string => {
  const countryCode = fieldValue as CountryCode | null;
  if (!countryCode) {
    return "";
  }
  const intl = i18nService.getIntl();
  if (!intl) {
    return countryCode;
  }
  return formatCountryName(intl, countryCode);
};

export const teamsFormatter = (entity: unknown, value: unknown): string =>
  (value as Team[]).map(({ name }) => name).join(", ");

export const filesFormatter = (entity: unknown, value: unknown): string => {
  const files = value as File[] | null;
  return files?.map((file) => file.name).join("; ") ?? "";
};

export const getFilesCsvFormatter =
  (entityType: EntityTypeSupportingFiles) =>
  (entity: unknown, value: unknown): string => {
    const files = value as File[] | null;
    return (
      files
        ?.map((file) => {
          let url = `https://${ConfigService.getBaseAppUrl()}`;
          switch (entityType) {
            case EntityType.ACTIVITY:
              url += `${Path.ACTIVITY}/${(entity as Activity).id}`;
              break;
            case EntityType.COMPANY:
              url += `${Path.COMPANY}/${(entity as Company).id}`;
              break;
            case EntityType.DEAL:
              url += `${Path.DEAL}/${(entity as Deal).id}`;
              break;
            case EntityType.PERSON:
              url += `${Path.PEOPLE}/${(entity as Person).id}`;
              break;
            case EntityType.TRIP:
              url += `${Path.REPORTS}/mileage-tracking/${(entity as Trip).id}`;
              break;
          }
          url += `/files/${file.id}`;
          return url;
        })
        .join(" ;") ?? ""
    );
  };

export const percentFormatter = (entity: unknown, value: unknown): string => {
  if (value === undefined) {
    return "";
  }
  const intl = i18nService.getIntl();
  if (intl) {
    return intl.formatNumber((value as number) / 100, { style: "percent" });
  }
  return String(value as number);
};

export const numberFormatter = (entity: unknown, value: unknown): string => {
  if (value === undefined) {
    return "";
  }
  const intl = i18nService.getIntl();
  if (intl) {
    return intl.formatNumber(value as number);
  }
  return String(value as number);
};
